/**
 * RPC 韧性层（v1.9.22）
 * ============================================================================
 * 背景：Deposit / Withdraw 曾出现 `Non-200 status code: '429'`。
 *   429 = RPC 节点限流（Too Many Requests）。该错误来自**钱包内置的 RPC**
 *   （MetaMask / Trust 等用 `wallet_addEthereumChain` 时我们传的 rpcUrls），
 *   而不是后端 —— 后端签名只读数据库，不发链上请求。
 *
 * 两类问题要分开治：
 *   1) 读请求（balanceOf / allowance / 回执轮询）不应占用钱包 RPC：
 *      → 用本文件的 getReadProvider()，基于多条公共 RPC 的 FallbackProvider，
 *        某条被限流会自动切下一条。
 *   2) 写请求（钱包弹窗发交易）绕不开钱包 RPC：
 *      → withRpcHeal()：命中 429 时用 wallet_addEthereumChain 轮换 RPC，
 *        等 1.5s 自动重试一次。
 */
import { ethers } from 'ethers';
import { Network } from '@/config/constants';

const ROTATE_PREFIX = 'predi_rpc_rotate:';

/** 当前链（十六进制 chainId）。由 store 在初始化 / 切链时写入 */
let activeChainId = null;

export function setActiveChainId(chainId) {
  if (chainId && chainId !== activeChainId) {
    activeChainId = chainId;
    // 切链后读节点也要重建
    readProvider = null;
    readProviderChainId = null;
  }
}

export function getActiveChainId() {
  if (activeChainId && Network[activeChainId]) {
    return activeChainId;
  }
  const keys = Object.keys(Network);
  return keys[0];
}

/** 某条链配置的 RPC 列表（永远返回非空数组） */
export function chainRpcUrls(chainId) {
  const net = Network[chainId] || Network[getActiveChainId()] || {};
  const urls = (net.rpcUrls || []).filter((u) => !!u);
  return urls.length ? urls : ['https://bsc-dataseed.binance.org'];
}

/** 429 / 限流类错误判定（不同钱包文案不同，统一按关键字兜底） */
export function isRateLimitError(err) {
  if (!err) {
    return false;
  }
  const texts = [
    err.message,
    err.reason,
    err.body,
    err.error && err.error.message,
    err.data && err.data.message,
    err.error && err.error.data && err.error.data.message,
    typeof err === 'string' ? err : '',
  ];
  return texts.some((t) => !!t && /(\b429\b|non-200 status code|too many requests|rate[-\s]?limit)/i.test(String(t)));
}

/** 连不上 / 超时 / 5xx 也值得换个节点重试 */
export function isTransientRpcError(err) {
  if (isRateLimitError(err)) {
    return true;
  }
  if (!err) {
    return false;
  }
  const text = String(err.message || err.reason || err);
  return /(timeout|timed out|failed to fetch|network error|socket hang up|\b50[0234]\b|SERVER_ERROR|bad response)/i.test(text);
}

let readProvider = null;
let readProviderChainId = null;

/**
 * 只读 provider：多节点 FallbackProvider（quorum=1 → 用一个节点的结果即可）。
 * 单条 RPC 被限流时自动切到下一条，页面数据与交易回执轮询不再依赖钱包。
 */
export function getReadProvider(chainId) {
  const id = chainId || getActiveChainId();
  if (readProvider && readProviderChainId === id) {
    return readProvider;
  }
  const urls = chainRpcUrls(id);
  const configs = urls.map((url, i) => ({
    provider: new ethers.providers.JsonRpcProvider(url),
    priority: i + 1,
    weight: 1,
    stallTimeout: 2500,
  }));
  readProvider = configs.length === 1
    ? configs[0].provider
    : new ethers.providers.FallbackProvider(configs, 1);
  readProviderChainId = id;
  return readProvider;
}

export function getReadContract(address, abi, chainId) {
  if (!address) {
    throw new Error('contract address missing');
  }
  return new ethers.Contract(address, abi, getReadProvider(chainId));
}

function readRotate(chainId) {
  try {
    return parseInt(window.localStorage.getItem(ROTATE_PREFIX + chainId) || '0', 10) || 0;
  } catch (e) {
    return 0;
  }
}

function writeRotate(chainId, idx) {
  try {
    window.localStorage.setItem(ROTATE_PREFIX + chainId, String(idx));
  } catch (e) {
    /* localStorage 不可用就算了，下次从 0 开始 */
  }
}

function walletProvider() {
  if (!window.ethereum) {
    return null;
  }
  return window.ethereum.providers ? window.ethereum.providers[0] : window.ethereum;
}

/**
 * 轮换钱包使用的 RPC。
 * `wallet_addEthereumChain` 对已存在的 chainId 会**更新**该网络的 rpcUrls，
 * 这是目前唯一能纠正「钱包里存着一条死掉/被限流节点」的官方手段。
 */
export async function healWalletRpc(chainId) {
  const provider = walletProvider();
  if (!provider) {
    return false;
  }
  const id = chainId || getActiveChainId();
  const net = Network[id];
  const urls = chainRpcUrls(id);
  if (!net || urls.length < 2) {
    return false;
  }
  const next = (readRotate(id) + 1) % urls.length;
  writeRotate(id, next);
  const params = [
    {
      chainId: id,
      chainName: net.chainName,
      nativeCurrency: net.nativeCurrency,
      rpcUrls: [urls[next]],
    },
  ];
  // 空数组会被部分钱包判为非法参数（新链可能还没有区块浏览器），有值才带
  if (Array.isArray(net.blockExplorerUrls) && net.blockExplorerUrls.length) {
    params[0].blockExplorerUrls = net.blockExplorerUrls;
  }
  try {
    await provider.request({ method: 'wallet_addEthereumChain', params });
  } catch (err) {
    console.warn('[rpc] healWalletRpc failed', err && err.message);
    return false;
  }
  // 有些钱包 add 后不会自动切过去；已在该链时这一步是无副作用的
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: id }] });
  } catch (err) {
    console.warn('[rpc] switch after heal failed', err && err.message);
  }
  return true;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * 执行一次可能触发 429 的钱包写操作；命中限流时换节点并重试一次。
 * 仍失败则抛出 code=RPC_RATE_LIMITED 的 Error，交给 UI 出人话提示。
 */
export async function withRpcHeal(fn, chainId) {
  try {
    return await fn();
  } catch (err) {
    if (!isRateLimitError(err)) {
      throw err;
    }
    const healed = await healWalletRpc(chainId);
    // 没换成功就多等一会儿（公共节点限流窗口通常是秒级）
    await sleep(healed ? 1500 : 4000);
    try {
      return await fn();
    } catch (err2) {
      if (isRateLimitError(err2)) {
        const e = new Error('RPC_RATE_LIMITED');
        e.code = 'RPC_RATE_LIMITED';
        e.cause = err2;
        throw e;
      }
      throw err2;
    }
  }
}
